
  import { defineComponent, PropType } from 'vue';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import { TReadableStorePaths } from '@/common/types/storePaths';
  import { getDateRangeForYear } from '@/common/utils/dates';

  import Loader from '@/components/Loader.vue';
  import ModalComponent from '@/components/modals/ModalComponent.vue';

  import { TActiveAgreementsFilterDatesParams } from '@/modules/ves/types/activeAgreements';

  import { VES_EVENT_STATUSES_IDS } from '../../../constants/constants';
  import { ALL_YEAR_OPTION, DASHBOARDS_INDICATOR_LINKS } from '../../../constants/dashboards';
  import { TBaseRegionFilter, TProjectBaseFilter } from '../../../types/common';
  import {
    TDashboardMainFilter,
    TDashboardMainFilterWithOptionalYear,
    TMainFilterPayload,
  } from '../../../types/dashboards';
  import { TEventsFilterDatesParams } from '../../../types/events';
  import DateActualDataSubtitle from '../../common/DateActualDataSubtitle.vue';
  import DashboardsIndicatorCards from '../dashboards-indicator-cards/DashboardsIndicatorCards.vue';
  import { TDashboardsIndicatorCardsConfig } from '../dashboards-indicator-cards/types';
  import { TModalsConfig } from './types';

  const modalTitles: Record<string, string> = {
    '0': 'Мероприятия в сфере ВЭД',
    '1': 'Количество соглашений субъектов РФ',
  };

  type TFilter = Partial<
    TDashboardMainFilter & TDashboardMainFilterWithOptionalYear & TBaseRegionFilter & { countryId: number }
  >;

  export default defineComponent({
    name: 'DashboardsIndicatorCardsWithModals',
    components: {
      DashboardsIndicatorCards,
      ModalComponent,
      DateActualDataSubtitle,
      ButtonComponent,
      Loader,
    },
    props: {
      filter: {
        type: Object as PropType<Partial<TFilter>>,
        required: true,
      },
      cardsConfig: {
        type: Object as PropType<TReadableStorePaths>,
        required: true,
      },
      modalsConfig: {
        type: Object as PropType<TModalsConfig>,
        required: true,
      },
      wrapInSection: {
        type: Boolean,
        default: true,
      },
    },
    data(): {
      config: TDashboardsIndicatorCardsConfig;
      listEventsAbortController: AbortController;
      isShowModal: boolean;
      isExistUpdates: boolean;
      isLoadingModal: boolean;
      selectedCardIndex: number | null;
    } {
      return {
        config: this.cardsConfig,
        listEventsAbortController: new AbortController(),
        isShowModal: false,
        isExistUpdates: true,
        isLoadingModal: false,
        selectedCardIndex: null,
      };
    },
    computed: {
      eventsCountByTypes() {
        return this.$store.getters[this.modalsConfig.eventsCountByTypes.getter];
      },
      agreementsCountByTypes() {
        return this.$store.getters[this.modalsConfig.agreementsCountByTypes.getter];
      },
      getModalTitle() {
        return modalTitles[String(this.selectedCardIndex)];
      },
      getRegionStatisticListEvents() {
        return this.selectedCardIndex === 1 ? this.agreementsCountByTypes : this.eventsCountByTypes;
      },
    },
    methods: {
      getEventsCountByTypes(payload: TMainFilterPayload) {
        return this.$store.dispatch(this.modalsConfig.eventsCountByTypes.action, payload);
      },
      getAgreementsCountByTypes(payload: TMainFilterPayload) {
        return this.$store.dispatch(this.modalsConfig.agreementsCountByTypes.action, payload);
      },
      openModalWindow(index: number) {
        this.isShowModal = true;

        if (this.selectedCardIndex !== index) {
          this.isExistUpdates = true;
        }

        this.selectedCardIndex = index;
      },
      redirectToDetailsPageByType(typeId: number) {
        const key = this.selectedCardIndex === 1 ? 'agreementTypeId' : 'typeId';
        const query = {
          [key]: typeId,
        };

        this.redirectToDetailsPage(query);
      },
      redirectToDetailsPage(query?: Record<string, string | number>) {
        const preparedQuery = {
          ...this.getLinkParams(this.selectedCardIndex ?? 0),
          ...query,
        };

        this.$router.push({
          name: DASHBOARDS_INDICATOR_LINKS[this.selectedCardIndex ?? 0],
          query: preparedQuery,
        });
      },
      async updateRegionStatisticListEvents() {
        this.listEventsAbortController.abort();
        this.listEventsAbortController = new AbortController();

        const payload: TMainFilterPayload = {
          params: {
            ...this.filter,
            year: this.filter.year ?? ALL_YEAR_OPTION.value,
          } as TMainFilterPayload['params'],
          abortController: this.listEventsAbortController,
        };

        const getFunc = this.selectedCardIndex === 1 ? this.getAgreementsCountByTypes : this.getEventsCountByTypes;

        try {
          this.isLoadingModal = true;
          await getFunc(payload);
        } finally {
          this.isLoadingModal = false;
          this.isExistUpdates = false;
        }
      },
      getLinkParams(index: number) {
        const isEventsCard = index === 0;
        const isAgreementsCard = index === 1;
        const regionRfParam = this.filter.regionRfId ? { regionRfId: this.filter.regionRfId } : {};
        const countryIdParam = this.filter.countryId ? { partnerCountryId: this.filter.countryId } : {};

        let query: Partial<
          TDashboardMainFilter &
            TBaseRegionFilter & {
              partnerCountryId: TProjectBaseFilter['partnerCountryId'];
              statusId: TProjectBaseFilter['statusId'];
            }
        > = {
          ...regionRfParam,
          ...countryIdParam,
        };

        // Год передаётся всегда (ТЗ): выбранное на «Обзоре» значение
        if (this.filter.year) {
          query = { ...query, year: this.filter.year };
        }

        // Дата/период — только для карточек мероприятий/соглашений и не для «За всё время».
        // Для текущего года диапазон = [01.01, сегодня], для прошлых = [01.01, 31.12] (см. getDateRangeForYear).
        if (this.filter.year && this.filter.year !== ALL_YEAR_OPTION.value) {
          let dateParams: TEventsFilterDatesParams | TActiveAgreementsFilterDatesParams | undefined;

          if (isEventsCard) {
            const { startDate, endDate } = getDateRangeForYear(this.filter.year);
            dateParams = { startDate, endDate };
          } else if (isAgreementsCard) {
            const { startDate, endDate } = getDateRangeForYear(this.filter.year);
            dateParams = {
              datePeriodFrom: startDate,
              datePeriodTo: endDate,
            };
          }

          query = {
            ...query,
            ...dateParams,
          };
        }

        if (isEventsCard) {
          query.statusId = VES_EVENT_STATUSES_IDS.COMPLETED;
        }

        return query;
      },
    },
    watch: {
      filter: {
        handler() {
          this.isExistUpdates = true;
        },
        deep: true,
      },
      isShowModal(value) {
        if (value && this.isExistUpdates) {
          this.updateRegionStatisticListEvents();
        }
      },
    },
  });
